import {
  Alert,
  Button,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { useState } from 'react'
import {
  useKitchens,
  useOutlets,
  useRemoveKitchen,
  useSaveKitchen,
  type Kitchen,
} from '@/api/hooks'
import { useAuthStore } from '@/auth/authStore'
import { EmptyState, ErrorBanner } from '@/components/common'

const TYPES = ['CENTRAL', 'OUTLET', 'SATELLITE'] as const

/**
 * The kitchens the cooking is filed against.
 *
 * <p>An order that is confirmed tells a kitchen what to cook, and the kitchen
 * it tells is the branch's own. Until a branch has one, nothing reaches the
 * production board at all - the order is confirmed, the day comes, and the
 * board is empty.</p>
 */
export function KitchensTab() {
  const canWrite = useAuthStore((state) => state.canWrite)
  const writable = canWrite('org')
  const { data: kitchens, error } = useKitchens()
  const { data: outlets } = useOutlets()
  const save = useSaveKitchen()
  const remove = useRemoveKitchen()

  const [outletId, setOutletId] = useState('')
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [kitchenType, setKitchenType] = useState<string>('OUTLET')

  const add = () => {
    if (!outletId || !code.trim() || !name.trim()) return
    save.mutate(
      { body: { outletId, code: code.trim().toUpperCase(), name: name.trim(), kitchenType } },
      {
        onSuccess: () => {
          setCode('')
          setName('')
        },
      },
    )
  }

  const edit = (kitchen: Kitchen, patch: Partial<Kitchen>) =>
    save.mutate({
      id: kitchen.id,
      body: {
        outletId: patch.outletId ?? kitchen.outletId,
        code: patch.code ?? kitchen.code,
        name: patch.name ?? kitchen.name,
        kitchenType: patch.kitchenType ?? kitchen.kitchenType,
        dailyCapacityPortions: patch.dailyCapacityPortions ?? kitchen.dailyCapacityPortions,
      },
    })

  const named = (id: string) => (outlets ?? []).find((one) => one.id === id)?.name ?? '—'

  return (
    <>
      <Typography variant="caption" color="text.secondary">
        Where the cooking happens. A confirmed order draws up what to cook and files it against the
        kitchen of the branch selling it — so a branch with no kitchen puts nothing on the
        production board, however many orders it takes.
      </Typography>

      <ErrorBanner error={error ?? save.error ?? remove.error} />

      {(kitchens ?? []).length === 0 ? (
        <Alert severity="warning" sx={{ mt: 2 }}>
          No kitchen is set up yet, so nothing an order sells can reach the production board. Add
          one for each branch that cooks.
        </Alert>
      ) : null}

      {writable ? (
        <Stack direction="row" spacing={2} alignItems="center" sx={{ my: 2 }} flexWrap="wrap" useFlexGap>
          <TextField
            select
            size="small"
            label="Branch"
            value={outletId}
            onChange={(event) => setOutletId(event.target.value)}
            sx={{ minWidth: 220 }}
          >
            {(outlets ?? []).map((outlet) => (
              <MenuItem key={outlet.id} value={outlet.id}>
                {outlet.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="Code"
            value={code}
            onChange={(event) => setCode(event.target.value)}
            placeholder="MAIN"
            sx={{ width: 120 }}
            inputProps={{ maxLength: 20 }}
          />
          <TextField
            size="small"
            label="Name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') add()
            }}
            placeholder="Main kitchen"
            sx={{ flexGrow: 1, minWidth: 200 }}
          />
          <TextField
            select
            size="small"
            label="Type"
            value={kitchenType}
            onChange={(event) => setKitchenType(event.target.value)}
            sx={{ width: 150 }}
          >
            {TYPES.map((type) => (
              <MenuItem key={type} value={type}>
                {type.charAt(0) + type.slice(1).toLowerCase()}
              </MenuItem>
            ))}
          </TextField>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            disabled={!outletId || !code.trim() || !name.trim() || save.isPending}
            onClick={add}
          >
            Add
          </Button>
        </Stack>
      ) : null}

      {(kitchens ?? []).length === 0 ? (
        <EmptyState message="No kitchen yet." />
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={120}>Code</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Branch</TableCell>
              <TableCell width={140}>Type</TableCell>
              <TableCell width={60} align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {(kitchens ?? []).map((kitchen) => (
              <TableRow key={kitchen.id} hover>
                <TableCell>{kitchen.code}</TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    size="small"
                    defaultValue={kitchen.name}
                    disabled={!writable}
                    inputProps={{ 'aria-label': `Name of ${kitchen.code}` }}
                    onBlur={(event) => {
                      if (event.target.value.trim() && event.target.value !== kitchen.name) {
                        edit(kitchen, { name: event.target.value.trim() })
                      }
                    }}
                  />
                </TableCell>
                <TableCell>{named(kitchen.outletId)}</TableCell>
                <TableCell>
                  {kitchen.kitchenType.charAt(0) + kitchen.kitchenType.slice(1).toLowerCase()}
                </TableCell>
                <TableCell align="right">
                  {writable ? (
                    /* Withdrawn rather than deleted: what it has cooked is
                       filed against it, and a report that cannot name the
                       kitchen a job came out of is one nobody can check. */
                    <Tooltip title="Take out of use. What it has cooked stays filed against it.">
                      <IconButton
                        size="small"
                        onClick={() => remove.mutate(kitchen.id)}
                        aria-label={`Withdraw ${kitchen.name}`}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </>
  )
}
